import React, { useEffect, useRef, useState } from "react";
import PrimaryButton from "../../../../shared/components/CustomButtons";
import { CustomInput } from "../../../../shared/components/AllInputs";
import { OverlayPanel } from "primereact/overlaypanel";
import { ProgressBar } from "primereact/progressbar";
import BotMsgIcon from "../../../../assets/icon/bot-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  chat,
  fetchChatHistory,
} from "../../../../store/slices/stageSmartSlice";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import moment from "moment/moment";
import { saveAs } from "file-saver";
import { getProfileAction } from "../../../../store/actions/user.actions";
import { createPaymentIntent } from "../../../../store/slices/servicesSlice";
import constants from "../../../../constants";

export default function Stagesmart() {
  const op = useRef(null);
  const history = useRef(null);
  const dispatch = useDispatch(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [typingText, setTypingText] = useState("");
  const [typingIndex, setTypingIndex] = useState(-1);
  const [typingSpeed] = useState(30);
  const [chatId, setChatId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [isImageValid, setIsImageValid] = useState(true);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [summaryVisible, setSummaryVisible] = useState(false);
  const [sortedHistory, setSortedHistory] = useState([])
  const keyframes = `
    @keyframes blink {
      from, to { opacity: 1; }
      50% { opacity: 0; }
    }
  `;

  const suggestions = [
    "Upload a photo to get started with staging ideas.",
    "Can you suggest the best design for this room?",
    "What are the trending designs in my area?",
  ];

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const chatHistory = useSelector((state) => state.stageSmart.history);
  const profile = useSelector((state) => state.user.profile);

  useEffect(() => {
    dispatch(fetchChatHistory());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getProfileAction());
  }, [dispatch]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    window.saveAs = saveAs;
    return () => {
      window.saveAs = undefined;
    };
  }, []);

  const supportedImageFormats = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
  ];

  const MAX_IMAGE_SIZE_MB = 4;
  const validateImageSize = (file) => {
    const maxSizeBytes = MAX_IMAGE_SIZE_MB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          isSquare: img.width === img.height,
        });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => resolve(null);
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    setUploadError(null);
    setIsImageValid(true);

    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];

    try {
      if (!supportedImageFormats.includes(file.type)) {
        throw new Error(
          "Unsupported file format. Please upload JPEG, PNG, GIF, or WEBP images."
        );
      }

      const previewUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Failed to read file."));
        reader.readAsDataURL(file);
      });

      setImagePreview(previewUrl);
      setSelectedImage(file);
      if (!validateImageSize(file)) {
        throw new Error(`Image must be less than ${MAX_IMAGE_SIZE_MB}MB`);
      }
      const dimensions = await getImageDimensions(file);
      if (!dimensions) {
        throw new Error("Failed to read image dimensions.");
      }
      const validDimensions = ["256x256", "512x512", "1024x1024"];
      const currentDimension = `${dimensions.width}x${dimensions.height}`;

      if (!validDimensions.includes(currentDimension)) {
        setImageDimensions(dimensions);
        setUploadError(
          "Image must be exactly 256×256, 512×512, or 1024×1024 pixels."
        );
        setIsImageValid(false);
        return;
      }
      setImageDimensions(dimensions);
      setUploadError(null);
      setIsImageValid(true);
    } catch (error) {
      setUploadError(error.message);
      setIsImageValid(false);
    }
  };

  const clearSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageDimensions(null);
    setUploadError(null);
    setIsImageValid(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const sendMessage = () => {
    if (inputValue.trim() === "" || isLoading || typingIndex !== -1) return;

    setIsLoading(true);
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: inputValue,
        image: imagePreview,
        dimensions: imageDimensions,
      },
      { role: "assistant", content: "" },
    ]);

    dispatch(
      chat({
        inputValue,
        image: selectedImage,
        dimensions: imageDimensions,
        chatId,
        setIsLoading,
        setChatId,
      })
    ).then((action) => {
      const isFulfilled = chat.fulfilled.match(action);
      const isRejected = chat.rejected.match(action);

      setMessages((prev) => {
        const updatedMessages = [...prev];
        const lastMessageIndex = updatedMessages.length - 1;

        if (
          lastMessageIndex >= 0 &&
          updatedMessages[lastMessageIndex].role === "assistant"
        ) {
          updatedMessages[lastMessageIndex] = {
            ...updatedMessages[lastMessageIndex],
            content:
              isFulfilled && action.payload?.response
                ? action.payload.response
                : isRejected
                  ? action.payload || "An error occurred."
                  : typeof action.payload === "string"
                    ? action.payload
                    : "Something went wrong.",
          };
        }
        return updatedMessages;
      });

      if (isFulfilled) {
        dispatch(getProfileAction());
      }

      setIsLoading(false);
    });

    setInputValue("");
    setSelectedImage(null);
    setImagePreview(null);
    setImageDimensions(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (
      typingIndex >= 0 &&
      typingIndex < messages.length &&
      messages[typingIndex].content
    ) {
      const fullText = messages[typingIndex].content;

      if (typingText.length < fullText.length) {
        const timeout = setTimeout(() => {
          setTypingText(fullText.substring(0, typingText.length + 1));
        }, typingSpeed);

        return () => clearTimeout(timeout);
      } else {
        setTypingIndex(-1);
      }
    }
  }, [typingText, typingIndex, messages, typingSpeed]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      sendMessage();
    }
  };

  const handleProceedToPay = async () => {
    try {
      setPaymentProcessing(true);
      setPaymentError(null);

      const amount = 10;

      const paymentIntent = await dispatch(
        createPaymentIntent(amount)
      ).unwrap();

      if (paymentIntent) {
        window.location.href = paymentIntent;
      } else {
        throw new Error("No payment URL received");
      }
      setPaymentProcessing(false);
    } catch (error) {
      console.error("Payment error:", error);
      setPaymentError(error.message || `Payment processing failed`);
      setPaymentProcessing(false);
    }
  };

  const formatResponse = (text) => {
    const imageUrlRegex = /^https?:\/\/.*\.(?:png|jpg|jpeg|gif|webp)(?:\?.*)?$/i;
    if (imageUrlRegex.test(text.trim())) {
      const imageUrl = text.trim();
      return `
        <div class="image-loading-container">
          <div style="position: relative;">
            <img 
              src="${imageUrl}" 
              class="w-full chat-img"
              alt="AI generated staged image"
              onload="
                this.style.display='block';
                this.parentNode.parentNode.style.background='none';
                const spinner = this.parentNode.parentNode.querySelector('.loading-spinner');
                const loadingText = this.parentNode.parentNode.querySelector('.loading-text');
                if (spinner) spinner.style.display = 'none';
                if (loadingText) loadingText.style.display = 'none';
                
                // Create download button
    //             if (!this.parentNode.querySelector('.download-btn')) {
    //               const downloadBtn = document.createElement('button');
    //               downloadBtn.className = 'download-btn';
    //               downloadBtn.innerHTML = '<i class=\\'pi pi-download\\'></i>';
    //               Object.assign(downloadBtn.style, {
    //                 position: 'absolute',
    //                 top: '8px',
    //                 right: '8px',
    //                 background: '#E00E3D',
    //                 color: 'white',
    //                 border: 'none',
    //                 borderRadius: '50%',
    //                 width: '32px',
    //                 height: '32px',
    //                 display: 'flex',
    //                 alignItems: 'center',
    //                 justifyContent: 'center',
    //                 cursor: 'pointer',
    //                 zIndex: '10'
    //               });
                  
    //              downloadBtn.addEventListener('click', async function(e) {
    //              e.preventDefault();
    //              e.stopPropagation();
    
    //             try {
    //                const fileName = 'staged-image-' + Date.now() + '.png';
    //               const response = await fetch('${imageUrl}');
        
    //                  if (!response.ok) {
    //                   throw new Error('Network response was not ok');
    //                       }
        
    //              const blob = await response.blob();
    //              const url = window.URL.createObjectURL(blob);
        
    //               const a = document.createElement('a');
    //               a.href = url;
    //               a.download = fileName;
    //              document.body.appendChild(a);
    //              a.click();
        
    //              // Clean up
    //     window.URL.revokeObjectURL(url);
    //     document.body.removeChild(a);
    // } catch (error) {
    //     console.error('Download failed:', error);
    //     alert('Download failed. Please try again.');
    // }
// });
                  
//                   this.parentNode.appendChild(downloadBtn);
//                 }
              "
              onerror="
                this.onerror=null;
                this.src='https://via.placeholder.com/500x300?text=Image+Failed+to+Load';
                this.style.display='block';
                this.parentNode.parentNode.style.background='none';
                const spinner = this.parentNode.parentNode.querySelector('.loading-spinner');
                const loadingText = this.parentNode.parentNode.querySelector('.loading-text');
                if (spinner) spinner.style.display = 'none';
                if (loadingText) loadingText.style.display = 'none';
              "
            />
          </div>
        </div>`;
    } else if (text.includes("uploads")) {
      const imageUrl = constants?.baseUrl + text
      return `
        <div class="image-loading-container">
          <div style="position: relative;">
            <img 
              src="${imageUrl}" 
              class="w-full chat-img"
              alt="AI generated staged image"
              onload="
                this.style.display='block';
                this.parentNode.parentNode.style.background='none';
                const spinner = this.parentNode.parentNode.querySelector('.loading-spinner');
                const loadingText = this.parentNode.parentNode.querySelector('.loading-text');
                if (spinner) spinner.style.display = 'none';
                if (loadingText) loadingText.style.display = 'none';
                
                // Create download button
    //             if (!this.parentNode.querySelector('.download-btn')) {
    //               const downloadBtn = document.createElement('button');
    //               downloadBtn.className = 'download-btn';
    //               downloadBtn.innerHTML = '<i class=\\'pi pi-download\\'></i>';
    //               Object.assign(downloadBtn.style, {
    //                 position: 'absolute',
    //                 top: '8px',
    //                 right: '8px',
    //                 background: '#E00E3D',
    //                 color: 'white',
    //                 border: 'none',
    //                 borderRadius: '50%',
    //                 width: '32px',
    //                 height: '32px',
    //                 display: 'flex',
    //                 alignItems: 'center',
    //                 justifyContent: 'center',
    //                 cursor: 'pointer',
    //                 zIndex: '10'
    //               });
                  
    //              downloadBtn.addEventListener('click', async function(e) {
    //              e.preventDefault();
    //              e.stopPropagation();
    
    //             try {
    //                const fileName = 'staged-image-' + Date.now() + '.png';
    //               const response = await fetch('${imageUrl}');
        
    //                  if (!response.ok) {
    //                   throw new Error('Network response was not ok');
    //                       }
        
    //              const blob = await response.blob();
    //              const url = window.URL.createObjectURL(blob);
        
    //               const a = document.createElement('a');
    //               a.href = url;
    //               a.download = fileName;
    //              document.body.appendChild(a);
    //              a.click();
        
    //              // Clean up
    //     window.URL.revokeObjectURL(url);
    //     document.body.removeChild(a);
    // } catch (error) {
    //     console.error('Download failed:', error);
    //     alert('Download failed. Please try again.');
    // }
// });
                  
//                   this.parentNode.appendChild(downloadBtn);
//                 }
              "
              onerror="
                this.onerror=null;
                this.src='https://via.placeholder.com/500x300?text=Image+Failed+to+Load';
                this.style.display='block';
                this.parentNode.parentNode.style.background='none';
                const spinner = this.parentNode.parentNode.querySelector('.loading-spinner');
                const loadingText = this.parentNode.parentNode.querySelector('.loading-text');
                if (spinner) spinner.style.display = 'none';
                if (loadingText) loadingText.style.display = 'none';
              "
            />
          </div>
        </div>`;
    }
    return text;
  };

  useEffect(() => {
    handleSortHistory()
  }, [chatHistory])

  const handleSortHistory = () => {
    const sortedList = [...chatHistory].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );

    setSortedHistory(sortedList)
  }

  return (
    <>
      <style>{keyframes}</style>
      <div className="container chat-bot-container">
        <div className="flex align-items-center justify-content-between py-3 gap-3">
          <PrimaryButton
            label="New Chat"
            icon="pi pi-comment"
            className="border-none bg-red"
            onClick={() => {
              setMessages([]);
              setChatId(null);
            }}
          />
          <div className="flex gap-3 align-items-center">
            <div
              className="primary-color cursor-pointer"
              onClick={(e) => history.current.toggle(e)}
            >
              Recent History <i className="pi pi-sort-down-fill"></i>
            </div>
            <div
              className={`border border-1 px-3 border-500 border-round-lg p-2 cursor-pointer ${profile?.totalQueries == 0 ? "" : "text-500"}`}
              onClick={(e) => { profile?.totalQueries == 0 && op.current?.toggle(e) }}
            >
              Buy Queries<i className="pi pi-bolt"></i>{" "}
              {profile?.totalQueries ?? "loading..."}
            </div>
            <OverlayPanel ref={op}>
              <h3 className="sub-heading m-0 text-2xl">
                {profile?.totalQueries ?? "loading..."} queries left
              </h3>
              <ProgressBar
                color="#E00E3D"
                className="mt-2"
                showValue={false}
                value={
                  ((profile?.totalQueries || 0) /
                    (profile?.purchasedQueries || 20)) *
                  100
                }
                style={{ height: "8px" }}
              />
              <div className="flex gap-3 mt-2">
                <p className="sub-heading">
                  You can buy 5 queries at just <strong>$10</strong>
                </p>
                <PrimaryButton
                  label="Buy Now"
                  extraClassNames="w-5"
                  onClick={() => setSummaryVisible(true)}
                />
              </div>
            </OverlayPanel>
          </div>
        </div>

        <div
          className="flex flex-column justify-content-between"
          style={{ height: "73vh" }}
        >
          {messages.length ? (
            <div
              ref={chatContainerRef}
              className="chat-messages p-3 overflow-auto"
              style={{ maxHeight: "60vh", overflowY: "auto" }}
            >
              {messages.map((msg, index) => (
                <React.Fragment key={index}>
                  {msg.role === "user" ? (
                    <div className="user-msg p-2 border-round-lg msg-container mb-4">
                      {msg.image && (
                        <div className="mb-2">
                          <img
                            src={msg.image}
                            alt="User uploaded"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              borderRadius: "8px",
                            }}
                          />
                        </div>
                      )}
                      {msg.content}
                    </div>
                  ) : (
                    <div className="bot-msg flex gap-2 align-items-start my-4">
                      <div className="bg-red flex align-items-center justify-content-center border-circle bot-icon">
                        <img src={BotMsgIcon} alt="" />
                      </div>
                      <div className="msg-container">
                        {msg.content ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: formatResponse(
                                // index === typingIndex ? typingText : msg.content
                                index === typingIndex ? typingText : msg?.image ? msg?.image : msg.content
                              ),
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <i
                              className="pi pi-spin pi-spinner"
                              style={{ fontSize: "1.5rem" }}
                            ></i>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className="content text-center mt-8 py-8">
              <h1>Stagesmart</h1>
              <p className="subtext">
                A real estate virtual staging assistant that provides detailed,
                realistic design suggestions.
              </p>
              <div className="suggestions justify-content-center flex flex-column lg:flex-row lg:gap-2">
                {suggestions.map((suggestion, index) => (
                  <p
                    key={index}
                    className="bg-gray p-4 text-gray-color border-round-lg"
                  >
                    {suggestion}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className={"relative flex flex-column chat-box"}>
            {imagePreview && (
              <div className="image-preview mb-2 p-2 border-1 border-round bg-white relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxHeight: "100px",
                    maxWidth: "100%",
                    aspectRatio: "1/1",
                    objectFit: "cover",
                    display: "block",
                    border: uploadError ? "2px solid red" : "none",
                  }}
                />
                {imageDimensions && (
                  <div className="image-dimensions text-xs">
                    {imageDimensions.width}×{imageDimensions.height}
                  </div>
                )}
                <button
                  className="p-button p-button-rounded p-button-danger justify-content-center p-button-sm w-3rem h-3rem"
                  style={{ position: "absolute", top: "5px", right: "5px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSelectedImage();
                  }}
                >
                  <i className="pi pi-times"></i>
                </button>
                {uploadError && (
                  <div className="error-message text-red-500 text-xs mt-1 mb-2">
                    <i className="pi pi-exclamation-triangle mr-1"></i>
                    {uploadError}
                  </div>
                )}
              </div>
            )}

            <div className="relative flex align-items-center ">
              <input
                type="file"
                ref={fileInputRef}
                accept=".jpg,.jpeg,.png,.gif,.webp"
                style={{ display: "none" }}
                onChange={handleFileSelect}
                key={selectedImage ? "file-input-1" : "file-input-0"}
              />
              <i
                className={`pi pi-paperclip absolute input-icon text-xl ${isLoading || typingIndex !== -1
                  ? "opacity-60"
                  : "cursor-pointer"
                  }`}
                onClick={() => {
                  if (isLoading || typingIndex !== -1) return;
                  fileInputRef.current.click();
                }}
                title="Attach image (JPEG, PNG, GIF, WEBP)"
                style={{
                  pointerEvents:
                    isLoading || typingIndex !== -1 ? "none" : "auto",
                }}
              ></i>
              <CustomInput
                extraClassName="w-full"
                inputClass="text-gray-color border-round-3xl p-3 px-7 w-full"
                placeholder="Message StageSmart"
                value={inputValue}
                disabled={isLoading || typingIndex !== -1}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
              />
              <div
                className={`bg-red absolute send-icon flex align-items-center justify-content-center border-circle ${isLoading || typingIndex !== -1 || !isImageValid
                  ? "opacity-60 cursor-not-allowed"
                  : "cursor-pointer"
                  } ml-2`}
                onClick={() => {
                  if (isLoading || typingIndex !== -1 || !isImageValid) return;
                  sendMessage();
                }}
                style={{
                  pointerEvents:
                    isLoading || typingIndex !== -1 || !isImageValid
                      ? "none"
                      : "auto",
                }}
              >
                <i className="pi pi-arrow-up text-white text-xl"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        header="Order Summary"
        headerClassName="text-center"
        visible={summaryVisible}
        style={{ width: "400px" }}
        breakpoints={{ "960px": "75vw", "641px": "99vw" }}
        onHide={() => {
          if (!summaryVisible) return;
          setSummaryVisible(false);
        }}
        draggable={false}
        resizable={false}
      >
        <div className="flex justify-content-between align-items-center gap-3 mb-3">
          <span className="sub-heading">Subtotal</span>
          <span className="sub-heading">$10</span>
        </div>
        <Divider />
        <div className="flex justify-content-between align-items-center gap-3 mb-4">
          <span className="font-bold sub-heading">Total</span>
          <span className="font-bold sub-heading">$10</span>
        </div>

        {paymentError && (
          <div className="p-message p-message-error mb-3">{paymentError}</div>
        )}

        <div className="flex justify-content-center align-items-center">
          <PrimaryButton
            label={paymentProcessing ? "Processing..." : "Proceed to Pay"}
            extraClassNames="w-8 mt-2"
            onClick={handleProceedToPay}
            disabled={paymentProcessing}
          />
        </div>
      </Dialog>
      <OverlayPanel
        className="history-chat"
        ref={history}
        style={{ width: "300px" }}
      >
        <div className="min-h-auto max-h-30rem overflow-auto">
          <h4 className="m-0 mb-4">History</h4>

          {sortedHistory?.map((history, index) => (
            <div key={index} className="overflow-y-auto">
              <h5 className="m-0">
                {moment(new Date(history?.date)).isSame(moment(), "day")
                  ? "Today"
                  : moment(new Date(history?.date)).format("MMM DD, YYYY")}
              </h5>

              {history?.chats?.map((chat, chatIndex) => (
                <ul key={chatIndex} className="p-0">
                  <li className="flex gap-2 p-1 align-items-center mb-1">
                    <span className=" text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                      {chat?.summary}
                    </span>
                    <p
                      className="border-1 border-400 p-1 m-0 px-2 border-round-lg text-sm cursor-pointer"
                      onClick={() => {
                        setChatId(chat?._id);
                        setMessages(chat?.messages);
                        history.current?.hide();
                      }}
                    >
                      Chat
                    </p>
                  </li>
                </ul>
              ))}
            </div>
          ))}
        </div>
      </OverlayPanel>
    </>
  );
}
